import { Check } from 'lucide-react';
import { ENUM_LABELS, type Consultant, type ReportView, PIN_PREFIX } from '#shared/types';
import { Badge } from '#client/components/ui/Badge';
import { Banner } from '#client/components/ui/Banner';
import { Card } from '#client/components/ui/Card';
import { Identifier } from '#client/components/ui/Identifier';
import { Select } from '#client/components/ui/Select';
import { formatAgeShort, formatDateTime } from '#client/lib/format';
import { RESOURCE } from '#client/lib/routes';
import { ReportNotesCard } from './ReportNotesCard';
import { ReportResultsTable } from './ReportResultsTable';
import type { ResultEntry } from './useResultEntry';
import { useAllRecords } from '#client/hooks/useAllRecords';

/* The cards and banners the result-entry screen is laid out from. */

export function EntryIdentity({ patient }: { patient: ReportView['patient'] }) {
  return (
    <span className="c-entry__identity">
      <strong>{patient.name}</strong>
      <Identifier value={patient.pin_no} stripPrefix={PIN_PREFIX} copyable />
      <span>
        {formatAgeShort(patient.age_year, patient.age_month, patient.age_days)}{' '}
        {ENUM_LABELS.gender[patient.gender]}
      </span>
      {patient.refer_by && <span>Ref: {patient.refer_by}</span>}
    </span>
  );
}

/** Why results cannot be edited, when they cannot: the report is locked, or the role is. */
export function EntryStatusBanner({
  report,
  canEdit,
  canReopen,
}: {
  report: Pick<ReportView, 'report_status' | 'completed_at'>;
  canEdit: boolean;
  canReopen: boolean;
}) {
  if (report.report_status === 'completed') {
    return (
      <Banner tone="success" title="This report is completed">
        Finished {formatDateTime(report.completed_at)}. Results are locked
        {canReopen ? ' — reopen it to make changes.' : '.'}
      </Banner>
    );
  }
  if (!canEdit) {
    return <Banner tone="info">Your role can view this report but not enter results.</Banner>;
  }
  return null;
}

export function ResultsCard({
  reportName,
  entry,
  canEdit,
}: {
  reportName: string;
  entry: Pick<
    ResultEntry,
    | 'entered'
    | 'total'
    | 'allEntered'
    | 'details'
    | 'detailById'
    | 'expanded'
    | 'setExpanded'
    | 'setResult'
    | 'setValueId'
    | 'makeKeyDownHandler'
    | 'inputRefs'
  >;
  canEdit: boolean;
}) {
  return (
    <Card
      title="Results"
      subtitle={`${entry.entered} of ${entry.total} entered`}
      actions={
        <Badge
          tone={entry.allEntered ? 'success' : 'draft'}
          icon={entry.allEntered ? <Check /> : undefined}
        >
          {entry.entered}/{entry.total}
        </Badge>
      }
      flush
    >
      <ReportResultsTable
        reportName={reportName}
        details={entry.details}
        detailById={entry.detailById}
        expanded={entry.expanded}
        onToggle={entry.setExpanded}
        onResultChange={entry.setResult}
        onRangeChange={entry.setValueId}
        makeKeyDownHandler={(index) => entry.makeKeyDownHandler(index, canEdit)}
        registerRef={(index) => (element) => {
          entry.inputRefs.current[index] = element;
        }}
        readOnly={!canEdit}
      />
    </Card>
  );
}

export function ConsultantCard({
  consultantId,
  onChange,
  canEdit,
}: {
  consultantId: number | null;
  onChange: (consultantId: number | null) => void;
  canEdit: boolean;
}) {
  // Active only: a retired doctor stays on the reports they already signed but
  // must not be offerable on new work.
  const consultants = useAllRecords<Consultant>({
    path: RESOURCE.consultants,
    extraQuery: { active: 'true' },
  });

  return (
    <Card
      title="Reporting doctor"
      subtitle="Signs this report. Defaulted from the lab test at registration."
    >
      <Select
        label="Reporting doctor"
        hideLabel
        options={consultants.items.map((consultant) => ({
          value: String(consultant.id),
          label: `${consultant.name} — ${consultant.designation}`,
        }))}
        placeholder="None"
        value={consultantId === null ? '' : String(consultantId)}
        onChange={(choice) => onChange(choice === '' ? null : Number(choice))}
        disabled={!canEdit}
        helpText={
          consultantId === null
            ? 'No doctor recorded — the printed report will carry no "Reported by" block.'
            : undefined
        }
      />
    </Card>
  );
}

/** The remark and comment pickers, in print order. */
export function EntryNotes({
  report,
  notes,
  canEdit,
}: {
  report: Pick<ReportView, 'remarks' | 'comments'>;
  notes: ResultEntry['notes'];
  canEdit: boolean;
}) {
  return (
    <>
      <ReportNotesCard
        title="Remarks"
        subtitle="Printed above the comments, with no heading"
        path={RESOURCE.reportRemarks}
        noun="remarks"
        attached={report.remarks}
        selectedIds={notes.remarkIds}
        onChange={notes.setRemarkIds}
        disabled={!canEdit}
      />

      <ReportNotesCard
        title="Comments"
        subtitle="Printed after the remarks"
        path={RESOURCE.reportComments}
        noun="comments"
        attached={report.comments}
        selectedIds={notes.commentIds}
        onChange={notes.setCommentIds}
        disabled={!canEdit}
      />
    </>
  );
}
